import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const LANG_LABEL = { vi: "Tiếng Việt", "zh-Hans": "简体中文", fr: "Français", es: "Español" };
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  needs_review: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  outdated: "bg-red-100 text-red-700 border-red-200",
};
const STATUS_LABEL = { draft: "Draft", needs_review: "Needs Review", approved: "Approved", outdated: "Outdated" };

export default function AdminTranslations() {
  const [lang, setLang] = useState("vi");
  const [page, setPage] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ lang });
    if (page) params.set("page", page);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    Promise.all([
      api.get(`/admin/i18n?${params.toString()}`),
      api.get(`/admin/i18n/stats`),
    ]).then(([r1, r2]) => { setItems(r1.data.items || []); setStats(r2.data.stats || {}); })
      .catch(() => toast.error("Could not load translations."))
      .finally(() => setLoading(false));
  }, [lang, page, status, q]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Object.keys(stats[lang] || {}).sort(), [stats, lang]);
  const keyId = (it) => `${it.lang}:${it.key}`;

  const save = async (it, newStatus) => {
    const id = keyId(it);
    const value = edits[id] !== undefined ? edits[id] : it.value;
    setSavingKey(id);
    try {
      const res = await api.patch(`/admin/i18n`, { lang: it.lang, key: it.key, value, status: newStatus || it.status });
      setItems((xs) => xs.map((x) => (keyId(x) === id ? res.data : x)));
      setEdits((e) => { const n = { ...e }; delete n[id]; return n; });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingKey(null); }
  };

  const bulkApprove = async () => {
    if (!window.confirm(`Approve & publish all translated ${LANG_LABEL[lang]} strings${page ? ` on "${page}"` : ""}? Legal, compliance and payment text should be reviewed first.`)) return;
    try {
      const res = await api.post(`/admin/i18n/bulk-approve`, { lang, page: page || null });
      toast.success(`Approved ${res.data.approved} strings`);
      load();
    } catch { toast.error("Bulk approve failed"); }
  };

  const syncSource = async () => {
    try { const res = await api.post(`/admin/i18n/sync-source`, {}); toast.success(`Source checked — ${res.data.outdated} flagged Outdated`); load(); }
    catch { toast.error("Sync failed"); }
  };

  const langStats = stats[lang] || {};
  const totals = Object.values(langStats).reduce((a, b) => ({ total: a.total + b.total, translated: a.translated + b.translated, approved: a.approved + b.approved }), { total: 0, translated: 0, approved: 0 });

  return (
    <div data-testid="admin-translations">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-foreground">Translations</h2>
          <p className="mt-1 text-sm text-muted-foreground">Edit, review and publish translated content. Initial drafts are machine-generated and <strong>not professionally reviewed</strong> — approve before publishing.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={syncSource} data-testid="i18n-sync"><RefreshCw className="h-4 w-4 mr-1.5" /> Check source changes</Button>
          <Button size="sm" className="rounded-sm bg-primary text-primary-foreground" onClick={bulkApprove} data-testid="i18n-bulk-approve"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve & publish</Button>
        </div>
      </div>

      {/* Coverage */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(LANG_LABEL).map(([code, label]) => {
          const s = stats[code] || {};
          const agg = Object.values(s).reduce((a, b) => ({ total: a.total + b.total, translated: a.translated + b.translated, approved: a.approved + b.approved }), { total: 0, translated: 0, approved: 0 });
          const pct = agg.total ? Math.round((agg.translated / agg.total) * 100) : 0;
          const apct = agg.total ? Math.round((agg.approved / agg.total) * 100) : 0;
          return (
            <button key={code} onClick={() => setLang(code)} className={`rounded-xl border p-4 text-left transition-colors ${lang === code ? "border-orange-400 bg-secondary" : "border-border bg-card hover:bg-secondary/50"}`} data-testid={`i18n-lang-${code}`}>
              <p className="text-sm font-medium" lang={code}>{label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{agg.translated}/{agg.total} translated · {pct}%</p>
              <p className="text-xs text-green-700">{apct}% approved</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Select value={page || "all"} onValueChange={(v) => setPage(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 h-9 rounded-sm bg-card" data-testid="i18n-filter-page"><SelectValue placeholder="All pages" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All pages</SelectItem>{pages.map((p) => <SelectItem key={p} value={p}>{p} ({langStats[p]?.translated || 0}/{langStats[p]?.total || 0})</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-9 rounded-sm bg-card" data-testid="i18n-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search key or text…" className="w-56 h-9 rounded-sm bg-card" data-testid="i18n-search" />
        <span className="text-xs text-muted-foreground ml-auto">{totals.translated}/{totals.total} translated · {totals.approved} approved</span>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center" data-testid="i18n-loading"><Loader2 className="h-5 w-5 mx-auto animate-spin text-orange-700" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="i18n-empty">No strings match your filters.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="i18n-list">
            {items.map((it) => {
              const id = keyId(it);
              const val = edits[id] !== undefined ? edits[id] : (it.value || "");
              const dirty = edits[id] !== undefined && edits[id] !== it.value;
              return (
                <div key={id} className="p-4 grid md:grid-cols-2 gap-3" data-testid={`i18n-row-${it.key}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono text-muted-foreground">{it.key}</span>
                      <Badge className="rounded-sm border bg-slate-50 text-slate-500 text-[10px]">{it.page}</Badge>
                      {it.status === "outdated" && <span className="inline-flex items-center gap-1 text-[10px] text-red-600"><AlertTriangle className="h-3 w-3" /> source changed</span>}
                    </div>
                    <p className="mt-1.5 text-sm text-foreground">{it.source_en}</p>
                  </div>
                  <div>
                    <Textarea value={val} onChange={(e) => setEdits((x) => ({ ...x, [id]: e.target.value }))} rows={2}
                      className="rounded-sm text-sm" lang={it.lang} placeholder="(missing — falls back to English)" data-testid={`i18n-input-${it.key}`} />
                    <div className="mt-2 flex items-center gap-2">
                      <Badge className={`rounded-sm border text-[10px] ${STATUS_STYLE[it.status]}`} data-testid={`i18n-status-${it.key}`}>{STATUS_LABEL[it.status]}</Badge>
                      <div className="ml-auto flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" disabled={savingKey === id} onClick={() => save(it)} data-testid={`i18n-save-${it.key}`}>{savingKey === id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}{dirty ? "Save*" : "Save"}</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => save(it, "needs_review")} data-testid={`i18n-review-${it.key}`}>Needs review</Button>
                        <Button size="sm" className="h-7 rounded-sm text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => save(it, "approved")} data-testid={`i18n-approve-${it.key}`}>Approve</Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
